import { Download, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import type { BotMode, TradeLedgerItem, TradeStatus } from "../../shared/contracts";
import { Metric } from "../components/data-display";
import { EmptyState, ErrorState, PageHeader } from "../components/page-state";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { type ActivitySnapshot, api, getErrorMessage } from "../lib/api";
import { formatDateTime, formatEth, formatSignedUsd, formatUsd, pnlTone } from "../lib/format";
import { cn } from "../lib/utils";

type ModeFilter = "all" | BotMode;
type StatusFilter = "all" | TradeStatus;

export function ActivityPage() {
  const [snapshot, setSnapshot] = useState<ActivitySnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<ModeFilter>("all");
  const [status, setStatus] = useState<StatusFilter>("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setSnapshot(await api.activity());
      setError(null);
    } catch (caught) {
      setError(getErrorMessage(caught));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let active = true;
    api.activity()
      .then((result) => {
        if (!active) return;
        setSnapshot(result);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (active) setError(getErrorMessage(caught));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, []);

  const trades = useMemo(() => {
    if (!snapshot) return [];
    return snapshot.trades.filter((trade) => (mode === "all" || trade.mode === mode) && (status === "all" || trade.status === status));
  }, [mode, snapshot, status]);

  const totals = useMemo(() => trades.reduce(
    (accumulator, trade) => ({
      net: accumulator.net + (trade.netPnlUsd ?? 0),
      fees: accumulator.fees + trade.feesUsd,
      closed: accumulator.closed + (trade.status === "closed" ? 1 : 0),
      live: accumulator.live + (trade.mode === "live" ? 1 : 0),
    }),
    { net: 0, fees: 0, closed: 0, live: 0 },
  ), [trades]);

  function exportCsv() {
    if (!trades.length) return;
    const header = ["id", "mode", "status", "closedAt", "quantityEth", "entryPrice", "exitPrice", "grossPnlUsd", "feesUsd", "netPnlUsd", "reason"];
    const rows = trades.map((trade) => header.map((key) => JSON.stringify(trade[key as keyof TradeLedgerItem] ?? "")).join(","));
    const blob = new Blob([[header.join(","), ...rows].join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "hyper-profits-activity.csv";
    anchor.click();
    URL.revokeObjectURL(url);
  }

  if (loading && !snapshot) {
    return <div className="grid gap-6"><Skeleton className="h-28" /><Skeleton className="h-28" /><Skeleton className="h-96" /></div>;
  }
  if (!snapshot) return <ErrorState message={error ?? "Activity could not be loaded."} onRetry={() => void load()} />;

  return (
    <div className="grid gap-8">
      <PageHeader
        eyebrow="ACCOUNT LEDGER"
        title="Every result, with fees included."
        description="Review paper and live trades without hiding Kraken fees behind gross performance."
        actions={
          <>
            <Button variant="outline" size="sm" onClick={() => void load()}><RefreshCw data-icon="inline-start" /> Refresh</Button>
            <Button variant="outline" size="sm" onClick={exportCsv} disabled={!trades.length}><Download data-icon="inline-start" /> Export CSV</Button>
          </>
        }
      />

      {error && (
        <div className="rounded-xl border border-coral/25 bg-coral-soft px-4 py-3 text-sm text-destructive" role="status">
          Activity refresh failed. The results below are from the last successful response.
        </div>
      )}

      <section className="grid grid-cols-2 gap-x-4 gap-y-7 border-b border-border pb-7 sm:grid-cols-4 sm:gap-x-5" aria-label="Filtered activity summary">
        <Metric label="Net P/L" value={formatSignedUsd(totals.net)} tone={pnlTone(totals.net)} detail="Selected results" />
        <Metric label="Kraken fees" value={formatUsd(totals.fees)} detail="Actual ledger fees" />
        <Metric label="Closed trades" value={String(totals.closed)} detail="Selected results" />
        <Metric label="Live trades" value={String(totals.live)} detail="Selected results" />
      </section>

      <section aria-labelledby="trade-ledger-title">
        <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-xs font-bold text-muted-foreground">TRADE LEDGER</p>
            <h2 id="trade-ledger-title" className="font-display mt-1 text-xl font-bold tracking-[-0.03em]">Orders and outcomes</h2>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className="grid gap-1 text-xs font-semibold text-muted-foreground">
              Mode
              <select className="h-11 min-w-28 rounded-lg border border-input bg-card px-3 text-sm text-foreground" value={mode} onChange={(event) => setMode(event.target.value as ModeFilter)}>
                <option value="all">All modes</option><option value="paper">Paper</option><option value="live">Live</option>
              </select>
            </label>
            <label className="grid gap-1 text-xs font-semibold text-muted-foreground">
              Status
              <select className="h-11 min-w-28 rounded-lg border border-input bg-card px-3 text-sm text-foreground" value={status} onChange={(event) => setStatus(event.target.value as StatusFilter)}>
                <option value="all">All statuses</option><option value="open">Open</option><option value="closed">Closed</option><option value="cancelled">Cancelled</option><option value="rejected">Rejected</option>
              </select>
            </label>
          </div>
        </div>

        {trades.length ? (
          <>
            <div className="hidden overflow-x-auto rounded-2xl border border-border bg-card md:block">
              <table className="w-full min-w-[1180px] border-collapse text-left text-sm">
                <caption className="sr-only">Hyper Profits trade ledger, including status, reason, Kraken fees, and net P/L</caption>
                <thead className="bg-muted/60 text-xs text-muted-foreground">
                  <tr>{["Closed", "Mode", "Status", "Reason", "Size", "Entry", "Exit", "Gross P/L", "Fees", "Net P/L"].map((label) => <th key={label} scope="col" className="px-4 py-3 font-semibold">{label}</th>)}</tr>
                </thead>
                <tbody>
                  {trades.map((trade) => (
                    <tr key={trade.id} className="border-t border-border align-top">
                      <td className="whitespace-nowrap px-4 py-4 text-xs text-muted-foreground">{formatDateTime(trade.closedAt)}</td>
                      <td className="px-4 py-4"><Badge variant={trade.mode === "live" ? "outline" : "secondary"}>{trade.mode}</Badge></td>
                      <td className="px-4 py-4"><TradeStatusBadge status={trade.status} /></td>
                      <td className="max-w-[18rem] px-4 py-4 text-xs leading-5 text-muted-foreground">{trade.reason}</td>
                      <td className="tabular-nums whitespace-nowrap px-4 py-4 font-semibold">{formatEth(trade.quantityEth)}</td>
                      <td className="tabular-nums px-4 py-4">{formatUsd(trade.entryPrice)}</td>
                      <td className="tabular-nums px-4 py-4">{formatUsd(trade.exitPrice)}</td>
                      <td className={cn("tabular-nums px-4 py-4 font-semibold", pnlTone(trade.grossPnlUsd) === "positive" && "text-positive", pnlTone(trade.grossPnlUsd) === "negative" && "text-destructive")}>{formatSignedUsd(trade.grossPnlUsd)}</td>
                      <td className="tabular-nums px-4 py-4">{formatUsd(trade.feesUsd)}</td>
                      <td className={cn("tabular-nums px-4 py-4 font-bold", pnlTone(trade.netPnlUsd) === "positive" && "text-positive", pnlTone(trade.netPnlUsd) === "negative" && "text-destructive")}>{formatSignedUsd(trade.netPnlUsd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <ul className="m-0 grid list-none gap-3 p-0 md:hidden">
              {trades.map((trade) => (
                <li key={trade.id} className="rounded-2xl border border-border bg-card p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant={trade.mode === "live" ? "outline" : "secondary"}>{trade.mode}</Badge>
                      <TradeStatusBadge status={trade.status} />
                    </div>
                    <span className="text-right text-xs text-muted-foreground">{formatDateTime(trade.closedAt)}</span>
                  </div>
                  <div className="mt-4 flex items-end justify-between gap-4"><div><p className="text-xs text-muted-foreground">Net P/L</p><p className={cn("tabular-nums mt-1 font-display text-xl font-bold", pnlTone(trade.netPnlUsd) === "positive" && "text-positive", pnlTone(trade.netPnlUsd) === "negative" && "text-destructive")}>{formatSignedUsd(trade.netPnlUsd)}</p></div><div className="text-right text-xs text-muted-foreground"><p>{formatEth(trade.quantityEth)}</p><p className="mt-1">{formatUsd(trade.feesUsd)} fees</p></div></div>
                  <p className="mt-4 border-t border-border pt-3 text-xs leading-5 text-muted-foreground">{trade.reason}</p>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <EmptyState title="No trades match these filters" description="Completed, cancelled, rejected, and currently open trades will appear here when they match the selected mode and status." />
        )}
      </section>

      <section className="border-t border-border pt-7" aria-labelledby="events-title">
        <p className="text-xs font-bold text-muted-foreground">SYSTEM EVENTS</p>
        <h2 id="events-title" className="font-display mt-1 text-xl font-bold tracking-[-0.03em]">Decision and connection log</h2>
        {snapshot.events.length ? (
          <ul className="m-0 mt-4 grid list-none p-0">
            {snapshot.events.map((event) => (
              <li key={event.id} className="grid gap-1 border-b border-border py-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:gap-5">
                <div><p className="text-sm font-semibold">{event.title}</p><p className="mt-1 text-xs leading-5 text-muted-foreground">{event.detail}</p></div>
                <time className="text-xs text-muted-foreground" dateTime={event.createdAt}>{formatDateTime(event.createdAt)}</time>
              </li>
            ))}
          </ul>
        ) : <p className="py-7 text-sm text-muted-foreground">No system events are available yet.</p>}
      </section>
    </div>
  );
}

function TradeStatusBadge({ status }: { status: TradeStatus }) {
  const variant = status === "open"
    ? "signal"
    : status === "rejected"
      ? "warning"
      : status === "cancelled"
        ? "outline"
        : "secondary";
  return <Badge variant={variant} className="capitalize">{status}</Badge>;
}
